import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MessageSquare, CalendarDays, Archive, ArchiveRestore, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getBoard, addBoardCard, editBoardCard, commentBoardCard, archiveBoardCard, getDiscordMe, getDiscordLinkCode, unlinkDiscord, setSessionFacility } from "@/lib/api";

// The gym's own task board (backend routes/kanban.py). Anyone on the team adds, moves and comments;
// drag a card to another column (or use the column picker inside it on a phone). Cards are archived,
// never deleted. Whoever is put on a card gets a Discord DM from the FPA Helper bot, once they've
// linked Discord here (Connect Discord: DM the bot "link <code>").

const err = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;
const field = "w-full bg-[#0a0a0a] border border-[#262626] rounded-md px-2.5 py-2 text-sm text-zinc-100";
const PRIORITY = {
  high: { label: "High", cls: "bg-rose-500/20 text-rose-200" },
  normal: { label: "Normal", cls: "bg-zinc-800 text-zinc-300" },
  low: { label: "Low", cls: "bg-zinc-900 text-zinc-500" },
};
const COLUMN_TINT = { todo: "border-t-zinc-500", doing: "border-t-sky-500", waiting: "border-t-amber-500", done: "border-t-emerald-500" };
const today = () => new Date().toLocaleDateString("en-CA");
const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

function CardTile({ card, names, onOpen, onDragStart }) {
  const overdue = card.due_date && card.status !== "done" && card.due_date < today();
  return (
    <button type="button" draggable onDragStart={(e) => onDragStart(e, card)} onClick={() => onOpen(card)}
      className="w-full text-left rounded-lg border border-[#262626] bg-[#0d0d0d] hover:border-[#3a3a3a] p-3 space-y-2 cursor-grab active:cursor-grabbing"
      data-testid={`board-card-${card.id}`}>
      <div className={`text-sm ${card.status === "done" ? "text-zinc-500 line-through" : "text-zinc-100"}`}>{card.title}</div>
      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
        {card.priority !== "normal" && <span className={`px-1.5 py-0.5 rounded ${PRIORITY[card.priority]?.cls}`}>{PRIORITY[card.priority]?.label}</span>}
        {(card.labels || []).map((l) => <span key={l} className="px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-200">{l}</span>)}
        {card.due_date && (
          <span className={`inline-flex items-center gap-1 ${overdue ? "text-rose-300" : "text-zinc-400"}`}>
            <CalendarDays className="h-3 w-3" />{shortDate(card.due_date)}
          </span>
        )}
        {(card.comments || []).length > 0 && <span className="inline-flex items-center gap-1 text-zinc-400"><MessageSquare className="h-3 w-3" />{card.comments.length}</span>}
        <span className="ml-auto flex -space-x-1">
          {(card.assignee_ids || []).map((u) => (
            <span key={u} title={names[u]} className="h-5 w-5 rounded-full bg-zinc-700 border border-[#0d0d0d] text-[9px] font-semibold text-zinc-100 grid place-items-center">
              {initials(names[u])}
            </span>
          ))}
        </span>
      </div>
    </button>
  );
}

function CardDialog({ card, board, onClose, onChanged }) {
  const [form, setForm] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(card ? { title: card.title, notes: card.notes || "", status: card.status, assignee_ids: card.assignee_ids || [],
      due_date: card.due_date || "", priority: card.priority || "normal", labels: (card.labels || []).join(", ") } : null);
    setComment("");
  }, [card]);
  if (!card || !form) return null;
  const canArchive = board.can_manage || card.created_by_id === board.me || (card.assignee_ids || []).includes(board.me);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await editBoardCard(card.id, { ...form, labels: form.labels.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Saved");
      onChanged(); onClose();
    } catch (e) { toast.error(err(e, "Couldn't save")); } finally { setSaving(false); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try { await commentBoardCard(card.id, comment.trim()); setComment(""); onChanged(); } catch (e) { toast.error(err(e, "Couldn't add the comment")); }
  };
  const archive = async (archived) => {
    try { await archiveBoardCard(card.id, archived); toast.success(archived ? "Archived" : "Back on the board"); onChanged(); onClose(); }
    catch (e) { toast.error(err(e, "Couldn't archive")); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="board-card-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="sr-only">Card</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">Added by {card.created_by} · {new Date(card.created_at).toLocaleDateString()}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input value={form.title} onChange={(e) => set("title", e.target.value)} className={`${field} text-base font-semibold`} disabled={card.archived} />
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={4} placeholder="Notes" className={field} disabled={card.archived} />
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-zinc-500 space-y-1">Column
              <select value={form.status} onChange={(e) => set("status", e.target.value)} className={field} disabled={card.archived}>
                {board.columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-zinc-500 space-y-1">Priority
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className={field} disabled={card.archived}>
                {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-zinc-500 space-y-1">Due
              <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className={field} disabled={card.archived} />
            </label>
            <label className="text-xs text-zinc-500 space-y-1">Labels
              <input value={form.labels} onChange={(e) => set("labels", e.target.value)} placeholder="equipment, members" className={field} disabled={card.archived} />
            </label>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-zinc-500">On it</div>
            <div className="flex flex-wrap gap-1.5">
              {board.team.map((t) => {
                const on = form.assignee_ids.includes(t.user_id);
                return (
                  <button key={t.user_id} type="button" disabled={card.archived}
                    onClick={() => set("assignee_ids", on ? form.assignee_ids.filter((x) => x !== t.user_id) : [...form.assignee_ids, t.user_id])}
                    title={t.discord ? "Gets a Discord message when put on a card" : "Hasn't connected Discord, so won't be told"}
                    className={`text-xs px-2.5 py-1 rounded-full border ${on ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-100" : "border-[#2a2a2a] text-zinc-400"}`}>
                    {t.name}{!t.discord && <span className="text-zinc-600"> · no Discord</span>}
                  </button>
                );
              })}
            </div>
          </div>
          {!card.archived && (
            <div className="flex gap-2">
              <button type="button" onClick={save} disabled={saving || !form.title.trim()}
                className="h-10 flex-1 rounded-full bg-zinc-100 text-zinc-900 font-semibold disabled:opacity-50" data-testid="board-card-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Save"}
              </button>
              {canArchive && (
                <button type="button" onClick={() => archive(true)} className="h-10 px-4 rounded-full border border-[#2a2a2a] text-zinc-300 inline-flex items-center gap-1.5">
                  <Archive className="h-4 w-4" />Archive
                </button>
              )}
            </div>
          )}
          {card.archived && canArchive && (
            <button type="button" onClick={() => archive(false)} className="h-10 w-full rounded-full border border-[#2a2a2a] text-zinc-200 inline-flex items-center justify-center gap-1.5">
              <ArchiveRestore className="h-4 w-4" />Put back on the board
            </button>
          )}
          <div className="border-t border-[#1f1f1f] pt-3 space-y-2">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Comments</div>
            {(card.comments || []).map((c) => (
              <div key={c.id} className="text-sm">
                <span className="text-zinc-200 font-medium">{c.by}</span>
                <span className="text-zinc-600 text-xs ml-2">{new Date(c.at).toLocaleString()}</span>
                <div className="text-zinc-300 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
            <div className="flex gap-2">
              <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder="Add a comment" className={field} data-testid="board-comment" />
              <button type="button" onClick={addComment} className="px-3 rounded-md border border-[#2a2a2a] text-zinc-200 text-sm">Add</button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DiscordConnect() {
  const [me, setMe] = useState(null);
  const [code, setCode] = useState(null);
  const refresh = useCallback(() => getDiscordMe().then(setMe).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {                       // waiting for the bot to see the code
    if (!code) return undefined;
    const t = setInterval(async () => { const m = await getDiscordMe().catch(() => null); if (m?.linked) { setMe(m); setCode(null); toast.success("Discord connected"); } }, 5000);
    return () => clearInterval(t);
  }, [code]);
  if (!me) return null;
  if (me.linked) {
    return (
      <button type="button" className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="discord-linked"
        title="Click to disconnect" onClick={async () => { await unlinkDiscord(); refresh(); toast.success("Discord disconnected"); }}>
        Discord: {me.discord_name || "connected"}
      </button>
    );
  }
  return (
    <div className="text-xs text-zinc-400 flex items-center gap-2 flex-wrap" data-testid="discord-connect">
      {!code && (
        <button type="button" className="px-3 h-8 rounded-full border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/10"
          onClick={async () => setCode((await getDiscordLinkCode()).code)}>Connect Discord to get told about your cards</button>
      )}
      {code && (
        <span>On Discord, send the <b className="text-zinc-200">FPA Helper</b> bot a direct message: <code className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-100 font-mono">link {code}</code> (works for 30 minutes)</span>
      )}
    </div>
  );
}

export default function Board() {
  const { activeFacility } = useAuth();
  const [params, setParams] = useSearchParams();
  const [board, setBoard] = useState(null);
  const [archived, setArchived] = useState(false);
  const [mine, setMine] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    try { setBoard(await getBoard(archived)); } catch (e) { toast.error(err(e, "Couldn't load the board")); }
  }, [archived]);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const names = useMemo(() => Object.fromEntries((board?.team || []).map((t) => [t.user_id, t.name])), [board]);
  const cards = useMemo(() => (board?.cards || []).filter((c) => !mine || (c.assignee_ids || []).includes(board.me) || c.created_by_id === board.me), [board, mine]);
  const open = (board?.cards || []).find((c) => c.id === openId) || null;

  // A link from a Discord message: /board?card=<id>&gym=<facility>
  useEffect(() => {
    const cardId = params.get("card");
    const gym = params.get("gym");
    if (!cardId) return;
    if (gym && activeFacility?.id && gym !== activeFacility.id) {      // the card is at another of their gyms
      setSessionFacility(gym).then(() => window.location.reload()).catch(() => toast.error("That card is at a gym you aren't on"));
      return;
    }
    if (board?.cards?.some((c) => c.id === cardId)) { setOpenId(cardId); setParams({}, { replace: true }); }
  }, [params, board, activeFacility?.id, setParams]);

  const add = async () => {
    if (!newTitle.trim()) return;
    try { await addBoardCard({ title: newTitle.trim(), assignee_ids: mine ? [board.me] : [] }); setNewTitle(""); load(); }
    catch (e) { toast.error(err(e, "Couldn't add the card")); }
  };
  const onDragStart = (e, card) => { e.dataTransfer.setData("text/plain", card.id); e.dataTransfer.effectAllowed = "move"; };
  const drop = async (e, status, beforeId) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const card = board.cards.find((c) => c.id === id);
    if (!card || id === beforeId) return;
    const col = board.cards.filter((c) => c.status === status && c.id !== id).sort((a, b) => a.order - b.order);
    const i = beforeId ? col.findIndex((c) => c.id === beforeId) : col.length;
    const prev = col[i - 1]?.order ?? (col[0] ? col[0].order - 1 : 0);
    const next = col[i]?.order;
    const order = next === undefined ? (col.length ? col[col.length - 1].order + 1 : 1) : (prev + next) / 2;
    if (card.status === status && card.order === order) return;
    setBoard((b) => ({ ...b, cards: b.cards.map((c) => (c.id === id ? { ...c, status, order } : c)) }));
    try { await editBoardCard(id, { status, order }); load(); } catch (er) { toast.error(err(er, "Couldn't move it")); load(); }
  };

  return (
    <div className="space-y-6" data-testid="board-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Board · {activeFacility?.name || "this gym"}</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Board</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">The team's to-dos for this gym. Drag a card to move it, or open it to assign people, set a due date and comment.</p>
          <div className="mt-2 flex items-center gap-4 flex-wrap">
            <DiscordConnect />
            <a href="/how-to/board.mp4" target="_blank" rel="noreferrer" className="text-xs text-zinc-400 hover:text-zinc-200 underline"
              data-testid="board-how-to">Watch the 3-minute how-to</a>
          </div>
        </div>
        <div className="flex items-center gap-1 card-surface p-1 rounded-full text-xs">
          {[["all", "Everyone's"], ["mine", "Mine"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMine(k === "mine")}
              className={`px-3 h-8 rounded-full font-medium ${(k === "mine") === mine ? "bg-zinc-100 text-zinc-900" : "text-zinc-400"}`}>{l}</button>
          ))}
          <button type="button" onClick={() => setArchived((a) => !a)} data-testid="board-archive-toggle"
            className={`px-3 h-8 rounded-full font-medium inline-flex items-center gap-1 ${archived ? "bg-zinc-100 text-zinc-900" : "text-zinc-400"}`}>
            <Archive className="h-3 w-3" />Archive
          </button>
        </div>
      </div>

      {!board && <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />}
      {board && archived && (
        <div className="space-y-2 max-w-2xl">
          {cards.length === 0 && <p className="text-sm text-zinc-500">Nothing archived.</p>}
          {cards.map((c) => <CardTile key={c.id} card={c} names={names} onOpen={(x) => setOpenId(x.id)} onDragStart={(e) => e.preventDefault()} />)}
        </div>
      )}
      {board && !archived && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
          {board.columns.map((col) => {
            const list = cards.filter((c) => c.status === col.key).sort((a, b) => a.order - b.order);
            return (
              <section key={col.key} onDragOver={(e) => { e.preventDefault(); setDragOver(col.key); }} onDragLeave={() => setDragOver(null)}
                onDrop={(e) => drop(e, col.key)} data-testid={`board-col-${col.key}`}
                className={`card-surface border-t-2 ${COLUMN_TINT[col.key]} p-3 space-y-2 min-h-[8rem] ${dragOver === col.key ? "ring-1 ring-zinc-500" : ""}`}>
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs uppercase tracking-[0.18em] text-zinc-400">{col.label}</span>
                  <span className="text-xs text-zinc-600 tabular-nums">{list.length}</span>
                </div>
                {col.key === "todo" && (
                  <div className="flex gap-1.5">
                    <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
                      placeholder="Add a card" className={`${field} py-1.5`} data-testid="board-new-title" />
                    <button type="button" onClick={add} className="px-2 rounded-md border border-[#2a2a2a] text-zinc-300" aria-label="Add card" data-testid="board-add">
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {list.map((c) => (
                  <div key={c.id} onDragOver={(e) => e.preventDefault()} onDrop={(e) => drop(e, col.key, c.id)}>
                    <CardTile card={c} names={names} onOpen={(x) => setOpenId(x.id)} onDragStart={onDragStart} />
                  </div>
                ))}
                {col.key === "done" && <p className="text-[11px] text-zinc-600 px-1">Done cards leave the board after {board.done_shown_days} days.</p>}
              </section>
            );
          })}
        </div>
      )}
      {board && open && <CardDialog card={open} board={board} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
